import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { supabaseClient } from "../../shared/supabase";
import {
  mapShopCatalogueProducts,
  ShopCatalogueProduct,
  ShopProductRecord,
} from "./catalogue";
import {
  AvailabilityBadge,
  Container,
  ErrorMessage,
  StateMessage,
} from "./shop.styles";

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 34px;
  justify-content: center;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 348px));
    gap: 48px;
  }

  @media (min-width: 1200px) {
    grid-template-columns: repeat(3, 348px);
  }
`;

const ProductCard = styled(Link)`
  color: inherit;
  display: flex;
  flex-direction: column;
  gap: 12px;
  text-decoration: none;
`;

const ProductImage = styled.img<{ $isSoldOut: boolean }>`
  aspect-ratio: 347 / 190;
  border: 0.5px solid #000;
  display: block;
  object-fit: cover;
  width: 100%;
  filter: ${(props) => (props.$isSoldOut ? "grayscale(0.35)" : "none")};

  @media (min-width: 768px) {
    aspect-ratio: 348 / 280;
  }
`;

const ProductMeta = styled.div`
  align-items: baseline;
  display: flex;
  gap: 16px;
  justify-content: space-between;
`;

const ProductName = styled.h2`
  color: #000;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.15;
  margin: 0;
  text-transform: uppercase;
`;

const ProductPrice = styled.span`
  color: #000;
  flex: 0 0 auto;
  font-size: 16px;
  font-weight: 600;
  line-height: 1;
`;

const ProductDescription = styled.p`
  color: #000;
  font-size: 14px;
  line-height: 1.35;
  margin: 0;
`;

const AvailabilityRow = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px 12px;
`;

const QuantityText = styled.span`
  color: #000;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.2;
`;

const ProductList = ({ products }: { products: ShopCatalogueProduct[] }) => {
  return (
    <ProductGrid>
      {products.map((product) => (
        <ProductCard key={product.id} to={`/shop/${product.id}`}>
          <ProductImage
            $isSoldOut={product.isSoldOut}
            alt={product.name}
            src={product.mainImageUrl}
          />
          <ProductMeta>
            <ProductName>{product.name}</ProductName>
            <ProductPrice>{product.priceLabel}</ProductPrice>
          </ProductMeta>
          <ProductDescription>{product.description}</ProductDescription>
          <AvailabilityRow>
            <AvailabilityBadge $isSoldOut={product.isSoldOut}>
              {product.availabilityLabel}
            </AvailabilityBadge>
            <QuantityText>
              Quantità disponibile: {product.availableQuantity}
            </QuantityText>
          </AvailabilityRow>
        </ProductCard>
      ))}
    </ProductGrid>
  );
};

export const Shop = () => {
  const [products, setProducts] = useState<ShopCatalogueProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const { data, error } = await supabaseClient
          .from("shop_products")
          .select(
            `
              id,
              name,
              description,
              main_image_url,
              price_cents,
              deleted_at,
              display_order,
              created_at,
              shop_product_variants (
                id,
                label,
                available_quantity,
                deleted_at
              )
            `,
          )
          .is("deleted_at", null)
          .order("display_order", { ascending: true })
          .order("created_at", { ascending: true });

        if (error) {
          throw error;
        }

        setProducts(
          mapShopCatalogueProducts((data ?? []) as ShopProductRecord[]),
        );
      } catch (err) {
        console.error("Error fetching shop products:", err);
        setError("Errore nel caricamento dei prodotti dello shop");
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  return (
    <Container>
      {loading && <StateMessage>Caricamento...</StateMessage>}
      {!loading && error && <ErrorMessage>{error}</ErrorMessage>}
      {!loading && !error && products.length === 0 && (
        <StateMessage>Nessun prodotto disponibile al momento</StateMessage>
      )}
      {!loading && !error && products.length > 0 && (
        <ProductList products={products} />
      )}
    </Container>
  );
};
